/**
 * Access-console mapping for the Administration > Access console.
 *
 * Binary access-grant model (spec 00_access-binary-grants.md, 2026-08-09):
 * every grantable tool carries its own `accessSlug` directly on the catalog
 * entry (`Tool.accessSlug` in shared/catalog.ts), namespaced `section.tool`
 * (e.g. `commerce.orders`, `stores.sweetspot`). A person either holds that
 * exact slug or does not; there are no levels. This file no longer maintains
 * a separate id -> scope map (the old `TOOL_SCOPE`) — the catalog IS the map.
 *
 * A handful of catalog tools intentionally have NO accessSlug: access is
 * automatic (the `core:read` employee baseline) or membership-based (enforced
 * server-side). Those are Chat, HR Directory, and Fleet Map — the fixed
 * `EVERYONE_TOOLS` set below. They still render in the console as a read-only
 * "Everyone" row so the console mirrors the sidebar exactly.
 *
 * Several catalog tools may share ONE accessSlug when they are really one
 * grantable unit — today that's `stores.sweetspot` (Today, Store thread,
 * Inventory Count, and the dedicated Sweet Spot console row): one checkbox in
 * the console, one grant row in the database, several UI surfaces. The console
 * shows exactly one row per accessSlug per department; see `buildGrantableDepts`.
 *
 * One tool is excluded from the grantable console on purpose: `admin.access`,
 * the Access console's own accessSlug. Rendering it as a checkbox would let
 * the console grant or revoke the ability to open the console, from inside
 * the console, which is a bootstrapping trap rather than a normal permission.
 * Every OTHER tool in the `administration` department (e.g. `admin.kb-feedback`,
 * the KB-article feedback box's gate) is an ordinary grantable entry.
 */

import { COCKPIT_DEPARTMENTS } from '@/data/tools.config';

// The one accessSlug that never renders as a console checkbox: see the
// module comment above. Not a whole-department skip — other `administration`
// tools (e.g. `admin.kb-feedback`) are ordinary grantable entries.
const SELF_REFERENTIAL_SLUGS: ReadonlySet<string> = new Set<string>(['admin.access']);

// Catalog tool ids that intentionally have NO accessSlug: access is automatic
// (the `core:read` employee baseline) or membership-based (enforced
// server-side), so there is nothing to grant. These still appear in the Access
// console as a read-only "Everyone" row so the console mirrors the sidebar
// exactly. Kept in sync with the catalog by the parity test in
// accessScopes.test.ts (every launched tool has an accessSlug or is here).
export const EVERYONE_TOOLS: ReadonlySet<string> = new Set<string>([
  'stores-chat', // Chat: core:read + server-side channel membership.
  'directory', // HR Directory: visible to every employee (core:read).
  'fleet-map', // Fleet Map: visible to every employee (core:read).
]);

// One console entry. A grantable entry (grantable true) renders a checkbox
// bound to `accessSlug`. A non-grantable entry (grantable false, from
// EVERYONE_TOOLS) has no assignable permission and renders as a read-only
// "Everyone" row (accessSlug is '').
export type GrantableEntry = {
  deptId: string;
  deptName: string;
  toolId: string;
  toolName: string;
  grantable: boolean;
  accessSlug: string;
};

export type GrantableDept = {
  deptId: string;
  deptName: string;
  entries: GrantableEntry[];
};

/**
 * Build the console entries grouped by department, reading everything live
 * from the catalog. Emits ONE entry per distinct accessSlug per department
 * (several catalog tools may share a slug — e.g. Today / Store thread /
 * Inventory Count all share `stores.sweetspot` with the dedicated Sweet Spot
 * row; the FIRST catalog tool carrying that slug wins the entry's display
 * name, which is why Sweet Spot is listed first in shared/catalog.ts), plus
 * one entry per Everyone tool. Excludes `admin.access` (see the module
 * comment above); every other tool, in every department including
 * `administration`, is an ordinary entry.
 */
export function buildGrantableDepts(): GrantableDept[] {
  const result: GrantableDept[] = [];
  for (const dept of COCKPIT_DEPARTMENTS) {
    const entries: GrantableEntry[] = [];
    const seenSlugs = new Set<string>();
    for (const tool of dept.tools) {
      if (tool.accessSlug && SELF_REFERENTIAL_SLUGS.has(tool.accessSlug)) continue;
      if (tool.accessSlug) {
        if (seenSlugs.has(tool.accessSlug)) continue; // dedupe shared-slug tools
        seenSlugs.add(tool.accessSlug);
        entries.push({
          deptId: dept.id,
          deptName: dept.name,
          toolId: tool.id,
          toolName: tool.name,
          grantable: true,
          accessSlug: tool.accessSlug,
        });
      } else if (EVERYONE_TOOLS.has(tool.id)) {
        entries.push({
          deptId: dept.id,
          deptName: dept.name,
          toolId: tool.id,
          toolName: tool.name,
          grantable: false,
          accessSlug: '',
        });
      }
      // A launched tool in neither the catalog (no accessSlug) nor
      // EVERYONE_TOOLS would be dropped here, but the parity test in
      // accessScopes.test.ts fails the build before that can ship.
    }
    if (entries.length > 0) {
      result.push({ deptId: dept.id, deptName: dept.name, entries });
    }
  }
  return result;
}

// Reverse lookup: accessSlug -> "Section: Tool" label. Used to label existing
// grants in the console (GrantRow pills). Falls back to the raw slug when no
// catalog tool carries it (so a stale/unmapped slug is still legible).
let _slugLabelCache: Map<string, string> | null = null;

function slugLabelMap(): Map<string, string> {
  if (_slugLabelCache) return _slugLabelCache;
  const map = new Map<string, string>();
  for (const dept of COCKPIT_DEPARTMENTS) {
    for (const tool of dept.tools) {
      if (!tool.accessSlug || map.has(tool.accessSlug)) continue; // first tool wins
      map.set(tool.accessSlug, `${dept.name}: ${tool.name}`);
    }
  }
  _slugLabelCache = map;
  return map;
}

export function slugLabel(slug: string): string {
  return slugLabelMap().get(slug) ?? slug;
}

// ----- Tool filter (Access console, Administration > Access) --------------

/**
 * The subset of an AccessGrant the tool filter needs. Kept structural rather
 * than importing AccessGrant from useAccess.ts, so this file (and its test)
 * stay decoupled from the react-query hooks module.
 */
export type ToolFilterGrant = {
  tools: string[];
  super_admin: boolean;
};

/**
 * AND semantics for the Access console's tool filter: with N tools selected,
 * a person is listed only when their grant holds every one of them. A
 * super-admin matches any filter, because super-admin bypasses every
 * per-tool grant, so filtering them out would misrepresent what they can
 * actually reach. Keep them visible with their super-admin badge.
 */
export function matchesToolFilter(grant: ToolFilterGrant, selected: string[]): boolean {
  if (selected.length === 0) return true;
  if (grant.super_admin) return true;
  return selected.every((slug) => grant.tools.includes(slug));
}
